import React, { useState } from 'react';
import { uploadPortfolio, bulkUploadPortfolio, BulkUploadItem } from '../services/portfolio';
import AnimatedSection from './AnimatedSection';

interface PortfolioUploadProps {
  onUploadSuccess?: () => void;
}

const PortfolioUpload: React.FC<PortfolioUploadProps> = ({ onUploadSuccess }) => {
  const [activeTab, setActiveTab] = useState<'single' | 'bulk'>('single');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Single upload form state
  const [formData, setFormData] = useState({
    media: null as File | null,
    title: '',
    description: '',
    category: '',
    websiteUrl: '',
    featured: false,
  });

  // Bulk upload state
  const [bulkData, setBulkData] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else if (type === 'file') {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        setFormData(prev => ({ ...prev, media: file }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSingleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.media) {
      setError('Please select a media file');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('media', formData.media);
      if (formData.title) formDataToSend.append('title', formData.title);
      if (formData.description) formDataToSend.append('description', formData.description);
      if (formData.category) formDataToSend.append('category', formData.category);
      if (formData.websiteUrl) formDataToSend.append('websiteUrl', formData.websiteUrl);
      formDataToSend.append('featured', formData.featured.toString());

      const response = await uploadPortfolio(formDataToSend);
      setSuccess(`Portfolio item uploaded successfully! ID: ${response.data.id}`);
      
      // Reset form
      setFormData({
        media: null,
        title: '',
        description: '',
        category: '',
        websiteUrl: '',
        featured: false,
      });
      const fileInput = document.getElementById('media') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
      // Notify parent to refresh list
      if (onUploadSuccess) {
        setTimeout(() => {
          onUploadSuccess();
        }, 1000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload portfolio item');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bulkData.trim()) {
      setError('Please enter portfolio items JSON');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const items: BulkUploadItem[] = JSON.parse(bulkData);
      const response = await bulkUploadPortfolio(items);
      setSuccess(`Bulk upload completed! Success: ${response.success}, Failed: ${response.failed}`);
      setBulkData('');
      
      // Notify parent to refresh list
      if (onUploadSuccess) {
        setTimeout(() => {
          onUploadSuccess();
        }, 1000);
      }
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError('Invalid JSON format. Please check your JSON syntax.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to bulk upload portfolio items');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatedSection animation="fade-in">
      <div className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-xl p-6 border border-primary-500/20 mb-8">
        <h3 className="text-2xl font-bold text-dark-900 dark:text-neutral-50 mb-6">Portfolio Management</h3>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-primary-500/20">
          <button
            onClick={() => setActiveTab('single')}
            className={`pb-3 px-4 font-medium transition-colors ${
              activeTab === 'single'
                ? 'text-primary-500 border-b-2 border-primary-500'
                : 'text-dark-600 dark:text-neutral-400 hover:text-primary-500'
            }`}
          >
            Single Upload
          </button>
          <button
            onClick={() => setActiveTab('bulk')}
            className={`pb-3 px-4 font-medium transition-colors ${
              activeTab === 'bulk'
                ? 'text-primary-500 border-b-2 border-primary-500'
                : 'text-dark-600 dark:text-neutral-400 hover:text-primary-500'
            }`}
          >
            Bulk Upload
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-500/10 border border-red-500/50 text-red-600 dark:text-red-400 rounded-lg">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-500/10 border border-green-500/50 text-green-600 dark:text-green-400 rounded-lg">
            {success}
          </div>
        )}

        {/* Single Upload Form */}
        {activeTab === 'single' && (
          <form onSubmit={handleSingleUpload} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-dark-900 dark:text-neutral-50 mb-2">
                Media File (Image/Video) *
              </label>
              <input
                type="file"
                id="media"
                accept="image/*,video/*"
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-primary-500/20 rounded-lg bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-900 dark:text-neutral-50 mb-2">
                Title
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-primary-500/20 rounded-lg bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
                placeholder="Project Title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-900 dark:text-neutral-50 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-2 border border-primary-500/20 rounded-lg bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
                placeholder="Project description"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-dark-900 dark:text-neutral-50 mb-2">
                  Category
                </label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-primary-500/20 rounded-lg bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
                  placeholder="e.g., Web Development"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-900 dark:text-neutral-50 mb-2">
                  Website URL
                </label>
                <input
                  type="url"
                  name="websiteUrl"
                  value={formData.websiteUrl}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-primary-500/20 rounded-lg bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
                  placeholder="https://example.com"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="featured"
                name="featured"
                checked={formData.featured}
                onChange={handleInputChange}
                className="w-4 h-4 text-primary-500 rounded focus:ring-primary-500"
              />
              <label htmlFor="featured" className="ml-2 text-sm text-dark-600 dark:text-neutral-300">
                Featured Item
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-6 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
            >
              {loading ? 'Uploading...' : 'Upload Portfolio Item'}
            </button>
          </form>
        )}

        {/* Bulk Upload Form */}
        {activeTab === 'bulk' && (
          <form onSubmit={handleBulkUpload} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-dark-900 dark:text-neutral-50 mb-2">
                Portfolio Items (JSON Array)
              </label>
              <textarea
                value={bulkData}
                onChange={(e) => setBulkData(e.target.value)}
                rows={12}
                className="w-full px-4 py-2 border border-primary-500/20 rounded-lg bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50 font-mono text-sm"
                placeholder={`[\n  {\n    "id": "project-1",\n    "title": "Project 1",\n    "description": "Description",\n    "type": "image",\n    "media": "/project1.jpg",\n    "category": "Web Development",\n    "websiteUrl": "https://example.com",\n    "featured": true\n  }\n]`}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-6 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
            >
              {loading ? 'Uploading...' : 'Bulk Upload Portfolio Items'}
            </button>
          </form>
        )}
      </div>
    </AnimatedSection>
  );
};

export default PortfolioUpload;

